import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Link } from 'react-router';

import { sortByClothNo } from 'modules/HorseRacing/utils/rideFilter';
import MetaDataWrapper from 'modules/Common/containers/MetaDataWrapper';
import FeedIcon from 'modules/Common/components/png/FeedIcon';
import CaretIcon from 'modules/Common/components/svg/CaretIcon';
import PrintButton from 'modules/Common/components/svg/PrintButton';
import { localTimeShort, localFullDate } from 'utils/LocalDateTimeHelper';
import { toSlug } from 'utils';
import { formatHeadgear, keepUpperCase, formatHorseAge, distanceFormat }  from 'modules/HorseRacing/utils';
import ordinalSuffix from "utils/OrdinalSuffix";
import {formatCurrency} from "utils/CurrencyFormatting";
import { canonicalLink } from 'utils/seoUtils';

require('./HorseRacingMeetingFastCards.scss');

function bracket(text) {
  if (text) {
    return `(${text})`;
  }
}

export default class HorseRacingMeetingFastCards extends Component {
  static propTypes = {
    routes: PropTypes.array,
    meeting: PropTypes.object
  };

  render() {
    if (!this.props.meeting) {

        const errorPixelSrc = `/1x1.gif?type=fast-cards&id=0&cb=${Date.now()}`;

    return (
      <div className="fast-cards">
        <MetaDataWrapper title={`Fast Cards - Error`}/>
        <div className="fast-cards-meeting-title">
          <h1 className="page-title">No Fast Cards Found</h1>
        </div>
        <div className="fast-cards-meeting-summary">
          <div className="fast-cards-meeting-summary-text">
            No meeting could be found to show fast cards for.
          </div>
        </div>
        <div>
            <img src={errorPixelSrc} width="1" height="1"/>
        </div>
      </div>
    );
    }

    const {
      routes,
      meeting: {
        meeting_summary: {
          meeting_reference: {
            id: meetingId
          },
          course: {
            name: courseName,
            feed_source: feedSource
          },
          date,
          going
        },
        races
      }
    } = this.props;

    const courseNameSlug = toSlug(courseName);

    const racecardUrl = (raceName, raceId) =>
      `/racing/racecards/${date}/${courseNameSlug}/racecard/${raceId}/${toSlug(raceName)}`;

    const statusRunnerMap = {
      RUNNER: true,
      NONRUNNER: false,
      // DOUBTFUL: true, // not currently used
      WITHDRAWN: false
    }

    const runnerFilter = isRunner => ({ride_status}) => statusRunnerMap[ride_status] === isRunner;

    races.forEach(race => {
      race.runners = race.rides.filter(runnerFilter(true)).sort(sortByClothNo);
      race.nonRunners = race.rides.filter(runnerFilter(false));
    });

    const helmetProps = {
      title: `Fast Cards - ${courseName} ${date}`,
      ...canonicalLink(routes, {date, courseSlug: courseNameSlug, meetingId})
    };

    return (
      <div className="fast-cards">
        <MetaDataWrapper helmetProps={helmetProps}/>
        <div className="fast-cards-meeting-title">
          <h1 className="page-title">{courseName} <small>{localFullDate(date)}</small></h1>
          <FeedIcon feed={feedSource}/>
        </div>
        <div className="fast-cards-meeting-summary">
          <div className="fast-cards-meeting-summary-text">
            Going: {going}
          </div>
          <div className="fast-cards-meeting-print">
            <PrintButton/>
          </div>
        </div>
        {races.map(({
          race_summary: {
            race_summary_reference: {
              id: raceId
            },
            name: raceName,
            time,
            distance,
            race_class,
            age
          },
          prizes: {
            prize = []
          } = {},
          betting_forecast,
          runners,
          nonRunners
        }) => <div key={raceId} className="fast-card-race">
          <div className="fast-card-race-title">
            <div className="fast-card-race-title-text">{localTimeShort(`${date}T${time}Z`)} {raceName}</div>
            <Link className="fast-card-race-title-link" to={racecardUrl(raceName, raceId)}>
              <CaretIcon type="full" direction="right"/>
              Full racecard
            </Link>
          </div>
          <div className="fast-card-race-meta">
            <span className="fast-card-meta-distance">{distanceFormat(distance)}</span>
            {race_class && <span className="fast-card-meta-class">(Class {race_class})</span>}
            {age && <span className="fast-card-meta-age">{formatHorseAge(age)}</span>}
            {prize.sort((a, b) => (a.position - b.position)).map(prize => (
              <span className="fast-card-meta-prize" key={prize.position}>
                <span className="fast-card-meta-prize-label">{ordinalSuffix(prize.position)}</span>
                {formatCurrency(...prize.prize.split(" "))}
              </span>)
            )}
          </div>
          {runners.map(({
            ride_reference: {
              id: rideId
            },
            cloth_number: clothNumber,
            draw_number: drawNumber,
            handicap,
            headgear = [],
            horse,
            horse: {
              formsummary: {
                display_text: formString
              },
              age
            },
            trainer,
            jockey,
            jockey_claim,
            race_history_stats = [],
            medication = []
          }) => <div key={rideId} className="fast-card-runner">
            <span className="fast-card-runner-cloth">{clothNumber}</span>
            <span className="fast-card-runner-draw">{bracket(drawNumber)}</span>
            <span className="fast-card-runner-form">{formString}</span>
            <span className="fast-card-runner-horse">
              <span className="fast-card-runner-horse-name"><HorseLink {...{horse}}/></span>
              <span className="fast-card-runner-horse-headgear">{formatHeadgear(headgear)}</span>
              { race_history_stats.length > 0 && race_history_stats.map(({type}) =>
                <span className="fast-card-runner-race-history-stats" key={type}>{keepUpperCase(type)}</span>)
              }
              { medication.length > 0 && medication.map(({short_name}) => 
                  <span className="fast-card-runner-race-medication-stats" key={short_name}>{keepUpperCase(short_name)}</span>
                )
              }
            </span>
            <span className="fast-card-runner-age">{age}</span>
            <span className="fast-card-runner-handicap">{handicap}</span>
            <span className="fast-card-runner-trainer">T: {trainer && <TrainerLink {...{trainer}}/>}</span>
            <span className="fast-card-runner-jockey">J: {jockey && <JockeyLink {...{jockey}}/>}{jockey_claim && <span className="fast-card-runner-jockey-claim">{bracket(jockey_claim)}</span>}</span>
          </div>)}
          {!!nonRunners.length &&
            <div>
              <div className="fast-card-sub-title">Non-Runners</div>
              {nonRunners.map(({
                ride_reference: {
                  id: rideId
                },
                cloth_number: clothNumber,
                horse,
                trainer,
                jockey
              }) => <div key={rideId} className="fast-card-non-runner">
                <span className="fast-card-runner-cloth">{clothNumber}</span>
                <span className="fast-card-runner-horse-name"><HorseLink {...{horse}}/></span>
                <span className="fast-card-runner-trainer">T: {trainer && <TrainerLink {...{trainer}}/>}</span>
                <span className="fast-card-runner-jockey">J: {jockey && <JockeyLink {...{jockey}}/>}{/* jockey claim ?? */}</span>
              </div>)}
            </div>
          }
          {!!betting_forecast &&
            <div>
              <div className="fast-card-sub-title">Forecast</div>
              <div className="fast-card-forecast">{betting_forecast}</div>
            </div>
          }
        </div>)}
      </div>
    );
  }
}

function HorseLink({horse: {horse_reference: {id}, name}}) {
  return <Link to={`/racing/profiles/horse/${id}`}>{name}</Link>
}
HorseLink.propTypes = {horse: PropTypes.object.isRequired};

function TrainerLink({trainer: {business_reference: {id}, name}}) {
  return <Link to={`/racing/profiles/trainer/${id}`}>{name}</Link>
}
TrainerLink.propTypes = {trainer: PropTypes.object.isRequired};

function JockeyLink({jockey: {person_reference: {id}, name}}) {
  return <Link to={`/racing/profiles/jockey/${id}`}>{name}</Link>
}
JockeyLink.propTypes = {jockey: PropTypes.object.isRequired};



// WEBPACK FOOTER //
// ./modules/HorseRacing/components/HorseRacingMeetingFastCards.js